import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from '@env';

import type { ApiErrorEnvelope, AuthErrorCode, AuthTokens, AuthUser, Role } from '@core/models';
import { isAuthErrorCode } from '@core/models';
import type { ToastSeverity } from './toast.service';

import { AuthStorageService } from './auth-storage.service';

export interface AuthFailure {
  code: AuthErrorCode | 'AUTH_UNKNOWN';
  message: string;
  severity: ToastSeverity;
  status: number;
  correlationId: string | null;
}

const REFRESH_LOCK = 'arq-auth-refresh';

function isValidSeverity(value: unknown): value is ToastSeverity {
  return value === 'info' || value === 'success' || value === 'warning' || value === 'error';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(AuthStorageService);

  private readonly _currentUser = signal<AuthUser | null>(null);
  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);
  readonly role = computed<Role | null>(() => this._currentUser()?.role ?? null);

  private readonly baseUrl = environment.apiUrl;
  private readonly apiOrigin = this.buildApiOrigin();
  private pendingRefresh: Promise<AuthTokens> | null = null;

  private buildApiOrigin(): string {
    try {
      return new URL(environment.apiUrl).origin;
    } catch {
      return environment.apiUrl;
    }
  }

  private withAbsoluteAvatar(user: AuthUser): AuthUser {
    if (!user.avatarUrl || user.avatarUrl.startsWith('http')) return user;
    return { ...user, avatarUrl: `${this.apiOrigin}${user.avatarUrl}` };
  }

  async login(email: string, password: string): Promise<AuthTokens> {
    const tokens = await firstValueFrom(
      this.http
        .post<AuthTokens>(
          `${this.baseUrl}/auth/login`,
          { email, password },
          { withCredentials: true },
        )
        .pipe(catchError((err: HttpErrorResponse) => throwError(() => this.mapError(err)))),
    );
    this.applyTokens(tokens);
    return tokens;
  }

  /**
   * Requests a temporary password for the given email. The API is anti-enumeration (always 200),
   * so the caller shows a generic confirmation regardless of whether the account exists.
   */
  async forgotPassword(email: string): Promise<void> {
    await firstValueFrom(
      this.http.post<{ requested: boolean }>(`${this.baseUrl}/auth/forgot-password`, { email }),
    );
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<void>(`${this.baseUrl}/auth/logout`, null, { withCredentials: true }),
      );
    } catch {
      // logout is best-effort; clear local state regardless
    } finally {
      this.clearSession();
    }
  }

  refresh(): Promise<AuthTokens> {
    if (this.pendingRefresh) {
      return this.pendingRefresh;
    }
    const request = (): Promise<AuthTokens> =>
      firstValueFrom(
        this.http
          .post<AuthTokens>(`${this.baseUrl}/auth/refresh`, null, { withCredentials: true })
          .pipe(
            tap((tokens) => this.applyTokens(tokens)),
            catchError((err: HttpErrorResponse) => throwError(() => this.mapError(err))),
          ),
      );
    // Tabs share the refresh cookie and the BE rotates it on every use: serialize refreshes across
    // tabs (Web Locks) so each one sends the latest cookie instead of a just-rotated one.
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    const serialized = async (): Promise<AuthTokens> =>
      locks ? locks.request(REFRESH_LOCK, request) : request();
    const pending = serialized().finally(() => {
      this.pendingRefresh = null;
    });
    this.pendingRefresh = pending;
    return pending;
  }

  loadCurrentUser(): Observable<AuthUser | null> {
    return this.http.get<AuthUser>(`${this.baseUrl}/auth/me`, { withCredentials: true }).pipe(
      tap((user) => this._currentUser.set(this.withAbsoluteAvatar(user))),
      catchError(() => {
        this.clearSession();
        return [null];
      }),
    );
  }

  /**
   * Merges fresh fields into the cached current user so UI bound to `currentUser()` (e.g. the
   * top-header avatar/name) updates in real time after a self profile update. No-op if logged out.
   */
  patchCurrentUser(patch: Partial<AuthUser>): void {
    const current = this._currentUser();
    if (!current) return;
    this._currentUser.set(this.withAbsoluteAvatar({ ...current, ...patch }));
  }

  clearSession(): void {
    this.storage.clear();
    this._currentUser.set(null);
  }

  private applyTokens(tokens: AuthTokens): void {
    this.storage.setAccessToken(tokens.accessToken);
    this._currentUser.set(this.withAbsoluteAvatar(tokens.user));
  }

  private mapError(err: HttpErrorResponse): AuthFailure {
    const body = err.error as ApiErrorEnvelope | null;
    const envelope = body?.error;
    const rawMessage = envelope?.message;
    const message = Array.isArray(rawMessage) ? rawMessage.join(', ') : (rawMessage ?? '');
    const severity: ToastSeverity = isValidSeverity(
      (envelope as { severity?: unknown } | undefined)?.severity,
    )
      ? (envelope as { severity: ToastSeverity }).severity
      : 'error';
    return {
      code: isAuthErrorCode(envelope?.code) ? envelope.code : 'AUTH_UNKNOWN',
      message,
      severity,
      status: err.status,
      correlationId: envelope?.correlationId ?? null,
    };
  }
}
